import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { bewertungContent } from '../data/pageContent';

const UnternehmensbewertungPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <Helmet>
        <title>{bewertungContent.title}</title>
        <meta name="description" content={bewertungContent.description} />
        <link rel="canonical" href="https://www.unternehmenbewerten.ch/unternehmensbewertung" />
      </Helmet>
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-16 lg:py-24 bg-gradient-to-b from-[#f8f9fa] to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <nav className="text-sm text-[#5a5d5b] mb-4">
                <Link to="/" className="hover:text-[#FF0000]">Home</Link>
                <span className="mx-2">/</span>
                <span>Unternehmensbewertung</span>
              </nav>
              <h1 className="text-4xl sm:text-5xl font-bold text-[#1a1c1b] mb-6">
                {bewertungContent.h1}
              </h1>
              <p className="text-lg text-[#5a5d5b] mb-4">
                {bewertungContent.intro}
              </p>
              <p className="text-[#5a5d5b] mb-8">
                {bewertungContent.introExtended}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/bewertung" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#FF0000] text-white font-semibold rounded-lg hover:bg-[#e60000] transition-colors">
                  Jetzt kostenlos Unternehmenswert berechnen
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Vier Bewertungsmethoden im Überblick */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1a1c1b] mb-4 text-center">
              Vier Bewertungsmethoden im Überblick
            </h2>
            <p className="text-[#5a5d5b] text-center mb-8 max-w-3xl mx-auto">
              {bewertungContent.methodsIntro}
            </p>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {bewertungContent.methods.map((method) => (
                <div key={method.title} className="bg-[#f8f9fa] rounded-xl p-6 border border-gray-100">
                  <h3 className="text-[#1a1c1b] font-semibold mb-2">{method.title}</h3>
                  <p className="text-[#5a5d5b] text-sm mb-4">{method.desc}</p>
                </div>
              ))}
            </div>

            {/* Methodentabelle */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-[#f8f9fa]">
                    <th className="border border-gray-200 p-3 text-left font-semibold text-[#1a1c1b]">Methode</th>
                    <th className="border border-gray-200 p-3 text-left font-semibold text-[#1a1c1b]">Am besten geeignet für</th>
                  </tr>
                </thead>
                <tbody>
                  {bewertungContent.methods.map((method) => (
                    <tr key={method.title}>
                      <td className="border border-gray-200 p-3 text-[#1a1c1b]">{method.title}</td>
                      <td className="border border-gray-200 p-3 text-[#5a5d5b]">{method.bestFor}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Wann brauche ich eine Unternehmensbewertung? */}
        <section className="py-16 bg-[#f8f9fa]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1a1c1b] mb-4 text-center">
              Wann brauche ich eine Unternehmensbewertung?
            </h2>
            <p className="text-[#5a5d5b] text-center mb-8 max-w-3xl mx-auto">
              {bewertungContent.useCasesIntro}
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {bewertungContent.useCases.map((useCase) => (
                <div key={useCase.title} className="bg-white rounded-xl p-6 border border-gray-100">
                  {useCase.link ? (
                    <Link to={useCase.link} className="text-[#FF0000] font-semibold mb-2 block hover:underline">{useCase.title}</Link>
                  ) : (
                    <h3 className="text-[#FF0000] font-semibold mb-2">{useCase.title}</h3>
                  )}
                  <p className="text-[#5a5d5b] text-sm">{useCase.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Ablauf einer Bewertung bei uns */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1a1c1b] mb-8 text-center">
              Ablauf einer Bewertung bei uns
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {bewertungContent.processSteps.map((step) => (
                <div key={step.step} className="bg-[#f8f9fa] rounded-xl p-6 border border-gray-100">
                  <div className="w-10 h-10 rounded-full bg-[#FF0000] text-white flex items-center justify-center font-bold mb-4">
                    {step.step}
                  </div>
                  <h3 className="text-[#1a1c1b] font-semibold mb-2">{step.title}</h3>
                  <p className="text-[#5a5d5b] text-sm">{step.desc}</p>
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/bewertung" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#FF0000] text-white font-semibold rounded-lg hover:bg-[#e60000] transition-colors">
                Jetzt kostenlos Unternehmenswert berechnen
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>

        {/* Warum eine professionelle Bewertung wichtig ist */}
        <section className="py-16 bg-[#f8f9fa]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1a1c1b] mb-6 text-center">
              Warum eine professionelle Bewertung wichtig ist
            </h2>
            <p className="text-[#5a5d5b] leading-relaxed">
              {bewertungContent.whyImportant}
            </p>
          </div>
        </section>

        {/* Häufige Fragen */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1a1c1b] mb-8 text-center">
              Häufige Fragen zur Unternehmensbewertung
            </h2>
            <div className="space-y-6">
              {bewertungContent.faq.map((item) => (
                <div key={item.question} className="bg-[#f8f9fa] rounded-xl p-6 border border-gray-100">
                  <h3 className="text-[#1a1c1b] font-semibold mb-3">{item.question}</h3>
                  <p className="text-[#5a5d5b] leading-relaxed">{item.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-[#1a1c1b]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Bereit für Ihre kostenlose Bewertung?
            </h2>
            <p className="text-gray-300 mb-8">
              Starten Sie jetzt und erhalten Sie in nur 3 Minuten eine erste Wertindikation.
            </p>
            <Link to="/bewertung" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#FF0000] text-white font-semibold rounded-lg hover:bg-[#e60000] transition-colors">
              Jetzt kostenlos bewerten
              <ArrowRight size={18} />
            </Link>
          </div>
        </section>
      </main>
      
      <Footer />
      {/* Schemas werden ausschliesslich via SSG geliefert - keine Duplikate durch React */}
    </div>
  );
};

export default UnternehmensbewertungPage;
